// ./src/hooks/useUserInvitations.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  UserInvitation,
  UserInvitationService,
  UserInvitationFilter,
  UserInvitationInput,
  Pagination,
  UserInvitationAcceptInput,
  User,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseUserInvitationsOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<UserInvitationFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Authentication status */
  isAuthenticated?: boolean;
}

export interface UseUserInvitationsReturn {
  // Data state
  userInvitation: UserInvitation | null;
  userInvitations: UserInvitation[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<UserInvitationFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  isResending: boolean;
  isAcceptingInvitation: boolean;
  removingInvitationId: string | null;
  resendingInvitationId: string | null;
  selectedUserInvitation: UserInvitation | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<UserInvitationFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<UserInvitation[]>;
  create: (params: UserInvitationInput) => Promise<UserInvitation | null>;
  update: (
    id: string,
    params: UserInvitationInput
  ) => Promise<UserInvitation | null>;
  remove: (id: string) => Promise<boolean>;
  removeMany: (ids: string[]) => Promise<boolean>;
  resend: (id: string) => Promise<UserInvitation | null>;
  acceptInvitation: (params: UserInvitationAcceptInput) => Promise<User | null>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Search and filter operations
  setFilters: (filters: Partial<UserInvitationFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedUserInvitation: (invitation: UserInvitation | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  invitationsByStatus: {
    active: UserInvitation[];
    expired: UserInvitation[];
  };
  invitationsByEmailDomain: Record<string, UserInvitation[]>;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "firstName", direction: "asc" }];

export const useUserInvitations = (
  options: UseUserInvitationsOptions = {}
): UseUserInvitationsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    isAuthenticated = false,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [userInvitation, setUserInvitation] = useState<UserInvitation | null>(
    null
  );
  const [userInvitations, setUserInvitations] = useState<UserInvitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<UserInvitationFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isAcceptingInvitation, setIsAcceptingInvitation] = useState(false);
  const [removingInvitationId, setRemovingInvitationId] = useState<
    string | null
  >(null);
  const [resendingInvitationId, setResendingInvitationId] = useState<
    string | null
  >(null);
  const [selectedUserInvitation, setSelectedUserInvitation] =
    useState<UserInvitation | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => userInvitations.length === 0 && !loading,
    [userInvitations.length, loading]
  );

  const invitationsByStatus = useMemo(() => {
    const now = new Date();
    return userInvitations.reduce(
      (acc, invitation) => {
        const expiresAt = invitation.expiresAt
          ? new Date(invitation.expiresAt)
          : null;
        if (expiresAt && expiresAt < now) {
          acc.expired.push(invitation);
        } else {
          acc.active.push(invitation);
        }
        return acc;
      },
      { active: [] as UserInvitation[], expired: [] as UserInvitation[] }
    );
  }, [userInvitations]);

  const invitationsByEmailDomain = useMemo(() => {
    return userInvitations.reduce((acc, invitation) => {
      const email = invitation.email || "";
      const domain = email.includes("@") ? email.split("@")[1] : "unknown";
      if (!acc[domain]) {
        acc[domain] = [];
      }
      acc[domain].push(invitation);
      return acc;
    }, {} as Record<string, UserInvitation[]>);
  }, [userInvitations]);

  // Helper function to update invitation in list if it exists, or add it if not
  const updateOrAddInvitationInList = useCallback(
    (updatedInvitation: UserInvitation) => {
      setUserInvitations((prev) => {
        const existingIndex = prev.findIndex(
          (inv) => inv.id === updatedInvitation.id
        );
        if (existingIndex >= 0) {
          const newInvitations = [...prev];
          newInvitations[existingIndex] = updatedInvitation;
          return newInvitations;
        } else {
          return [updatedInvitation, ...prev];
        }
      });
    },
    []
  );

  // Helper function to remove invitation from list if it exists
  const removeInvitationFromList = useCallback((invitationId: string) => {
    setUserInvitations((prev) => prev.filter((inv) => inv.id !== invitationId));
  }, []);

  // Helper function to remove multiple invitations from list
  const removeInvitationsFromList = useCallback((invitationIds: string[]) => {
    const idsSet = new Set(invitationIds);
    setUserInvitations((prev) =>
      prev.filter((inv) => !idsSet.has(inv.id || ""))
    );
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setUserInvitation(null);
    setUserInvitations([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setIsResending(false);
    setIsAcceptingInvitation(false);
    setRemovingInvitationId(null);
    setResendingInvitationId(null);
    setSelectedUserInvitation(null);
  }, []);

  // Get single user invitation (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await UserInvitationService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const invitationData = result.data[0] as UserInvitation;
          setUserInvitation(invitationData);
          updateOrAddInvitationInList(invitationData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load user invitation";
          setLastError(errorMessage);
          setUserInvitation(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load user invitation";
        setLastError(errorMessage);
        setUserInvitation(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInvitationInList]
  );

  // List user invitations (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<UserInvitationFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setUserInvitations([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await UserInvitationService.list(
          filter as UserInvitationFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newInvitations = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setUserInvitations((prev) => {
              const existingIds = new Set(prev.map((inv) => inv.id));
              const uniqueNewInvitations = newInvitations.filter(
                (inv: any) => !existingIds.has(inv.id)
              );
              return [...prev, ...uniqueNewInvitations];
            });
          } else {
            setUserInvitations(newInvitations);
          }

          setHasMore(newInvitations.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load user invitations";
          setLastError(errorMessage);
          if (!isAppending) setUserInvitations([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load user invitations";
        setLastError(errorMessage);
        if (!isAppending) setUserInvitations([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many user invitations by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<UserInvitation[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await UserInvitationService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedInvitations = Array.isArray(result.data)
            ? result.data
            : [];
          loadedInvitations.forEach((invitation: any) => {
            updateOrAddInvitationInList(invitation);
          });
          setLastError(null);
          return loadedInvitations;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load user invitations";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load user invitations";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInvitationInList]
  );

  // Create user invitation (matching API method name)
  const create = useCallback(
    async (params: UserInvitationInput): Promise<UserInvitation | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await UserInvitationService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newInvitation = result.data[0] as UserInvitation;
          setUserInvitation(newInvitation);
          setUserInvitations((prev) => [newInvitation, ...prev]);
          setLastError(null);
          return newInvitation;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to create user invitation";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to create user invitation";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update user invitation (matching API method name)
  const update = useCallback(
    async (
      id: string,
      params: UserInvitationInput
    ): Promise<UserInvitation | null> => {
      if (!id) {
        setLastError("User invitation ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await UserInvitationService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedInvitation = result.data[0] as UserInvitation;
          setUserInvitation(updatedInvitation);
          updateOrAddInvitationInList(updatedInvitation);
          setLastError(null);
          return updatedInvitation;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to update user invitation";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update user invitation";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddInvitationInList]
  );

  // Resend user invitation (unique to UserInvitation)
  const resend = useCallback(
    async (id: string): Promise<UserInvitation | null> => {
      if (!id) {
        setLastError("User invitation ID is required for resending");
        return null;
      }

      setIsResending(true);
      setResendingInvitationId(id);
      setLastError(null);

      try {
        const result = await UserInvitationService.resend(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedInvitation = result.data[0] as UserInvitation;
          setUserInvitation(updatedInvitation);
          updateOrAddInvitationInList(updatedInvitation);
          setLastError(null);
          return updatedInvitation;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to resend user invitation";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to resend user invitation";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsResending(false);
        setResendingInvitationId(null);
      }
    },
    [updateOrAddInvitationInList]
  );

  // Accept  invitation
  const acceptInvitation = useCallback(
    async (params: UserInvitationAcceptInput): Promise<User | null> => {
      if (!params) {
        setLastError("User details are required for accepting invitation");
        return null;
      }

      setIsAcceptingInvitation(true);
      setLastError(null);

      try {
        const result = await UserInvitationService.acceptInvitation(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const user = result.data[0] as User;

          setLastError(null);
          return user;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to accept invitation";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to accept invitation";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsAcceptingInvitation(false);
      }
    },
    []
  );

  // Remove user invitation (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("User invitation ID is required for removal");
        return false;
      }

      setRemovingInvitationId(id);
      setLastError(null);

      try {
        const result = await UserInvitationService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setUserInvitation(null);
          removeInvitationFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to remove user invitation";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove user invitation";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingInvitationId(null);
      }
    },
    [removeInvitationFromList]
  );

  // Remove many user invitations (matching API method name)
  const removeMany = useCallback(
    async (ids: string[]): Promise<boolean> => {
      if (ids.length === 0) {
        setLastError("At least one user invitation ID is required for removal");
        return false;
      }

      setLastError(null);

      try {
        const result = await UserInvitationService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setUserInvitation(null);
          removeInvitationsFromList(ids);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to remove user invitations";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove user invitations";
        setLastError(errorMessage);
        return false;
      }
    },
    [removeInvitationsFromList]
  );

  // Load more user invitations
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh user invitations
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && isAuthenticated && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, isAuthenticated, hasAutoLoaded, list, pageSize, initialSort]);

  return {
    userInvitation,
    userInvitations,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    isResending,
    isAcceptingInvitation,
    removingInvitationId,
    resendingInvitationId,
    selectedUserInvitation,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    removeMany,
    resend,
    acceptInvitation,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedUserInvitation,
    reset,
    invitationsByStatus,
    invitationsByEmailDomain,
    lastError,
    clearError,
  };
};
